import { MapPin, Phone, Clock, Star, Navigation, ExternalLink } from "lucide-react";
import { CLIENT_REVIEWS } from "@/lib/clientReviews";

/**
 * Google Maps focused section for the homepage.
 *
 * Combines an embedded map of the Colorado Springs HQ with NAP, hours,
 * directions CTA, and 3 short review highlights. Designed to reinforce
 * local-pack signals (visible NAP + map + reviews) and provide AI/LLM
 * crawlers with a single, high-density block of citation-ready content.
 *
 * The matching JSON-LD LocalBusiness node is injected from
 * `src/routes/index.tsx` so the visible reviews and the structured-data
 * reviews stay in sync.
 */

const HQ = {
  name: "Colorado Web Impressions",
  street: "3210 E Woodmen Rd STE 110",
  city: "Colorado Springs",
  region: "CO",
  postal: "80920",
  phone: "(719) 419-3935",
  phoneTel: "+17194193935",
  hours: "Mon–Fri · 9:00 AM – 5:00 PM",
  lat: 38.9359976,
  lng: -104.7653118,
} as const;

const FULL_ADDRESS = `${HQ.street}, ${HQ.city}, ${HQ.region} ${HQ.postal}`;
const MAPS_QUERY = encodeURIComponent(`${HQ.name}, ${FULL_ADDRESS}`);
// Use maps.google.com (not www.google.com/maps) — the former renders inside
// iframes without an API key, the latter is blocked by X-Frame-Options.
const EMBED_SRC = `https://maps.google.com/maps?q=${MAPS_QUERY}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
const DIRECTIONS_HREF = `https://www.google.com/maps/dir/?api=1&destination=${MAPS_QUERY}`;

function truncate(s: string, max = 140) {
  if (s.length <= max) return s;
  return s.slice(0, max).replace(/\s+\S*$/, "") + "…";
}

export function GoogleMapsSection() {
  const highlights = [...CLIENT_REVIEWS]
    .filter((r) => r.rating === 5)
    .sort((a, b) => b.datePublished.localeCompare(a.datePublished))
    .slice(0, 3);

  return (
    <section className="bg-section-alt py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid items-start gap-12 lg:grid-cols-2">
          {/* LEFT: NAP + reviews */}
          <div>
            <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              📍 Find us on Google Maps
            </span>
            <h2 className="mt-5 text-4xl font-extrabold leading-[1.1] text-foreground sm:text-5xl">
              Visit us in{" "}
              <span className="text-gradient-gold">Colorado Springs</span>
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              Headquartered just off Powers in Colorado Springs — and serving every Front
              Range and mountain-town business in Colorado. Stop by, call, or get directions
              below.
            </p>

            {/* NAP card */}
            <div className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-card">
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-gold-deep" aria-hidden="true" />
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    Address
                  </div>
                  <a
                    href={DIRECTIONS_HREF}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-0.5 block font-semibold text-foreground transition-smooth hover:text-brand-gold-deep"
                  >
                    {HQ.street}
                    <br />
                    {HQ.city}, {HQ.region} {HQ.postal}
                  </a>
                </div>
              </div>

              <div className="mt-5 flex items-start gap-3">
                <Phone className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-gold-deep" aria-hidden="true" />
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    Phone
                  </div>
                  <a
                    href={`tel:${HQ.phoneTel}`}
                    className="mt-0.5 block font-semibold text-foreground transition-smooth hover:text-brand-gold-deep"
                   aria-label="Call (719) 419-3935">
                    {HQ.phone}
                  </a>
                </div>
              </div>

              <div className="mt-5 flex items-start gap-3">
                <Clock className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-gold-deep" aria-hidden="true" />
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    Hours
                  </div>
                  <div className="mt-0.5 font-semibold text-foreground">{HQ.hours}</div>
                </div>
              </div>

              {/* Rating bar */}
              <div className="mt-6 flex items-center gap-2 border-t border-border pt-5 text-sm">
                <span className="flex" aria-label="Five out of five stars">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className="h-4 w-4 fill-brand-gold text-brand-gold"
                      aria-hidden="true"
                    />
                  ))}
                </span>
                <span className="font-semibold text-foreground">5.0</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground">150+ Google reviews</span>
              </div>

              {/* CTAs */}
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <a
                  href={DIRECTIONS_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-gradient-gold px-5 py-3 text-sm font-bold uppercase tracking-wider text-brand-black shadow-gold transition-smooth hover:-translate-y-0.5"
                >
                  <Navigation className="h-4 w-4" aria-hidden="true" />
                  Get directions
                </a>
                <a
                  href="https://g.page/r/coloradowebimpressions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-md border border-border bg-background px-5 py-3 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold hover:text-brand-gold-deep"
                >
                  View on Google
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            </div>

            {/* Review highlights */}
            <div className="mt-8 space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                Recent review highlights
              </h3>
              {highlights.map((r) => (
                <article
                  key={`${r.author}-${r.datePublished}`}
                  className="rounded-xl border border-border bg-card p-5 shadow-card"
                >
                  <div className="flex" aria-label={`${r.rating} out of 5 stars`}>
                    {Array.from({ length: r.rating }).map((_, i) => (
                      <Star
                        key={i}
                        className="h-3.5 w-3.5 fill-brand-gold text-brand-gold"
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/85">
                    "{truncate(r.body, 160)}"
                  </p>
                  <footer className="mt-3 text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{r.author}</span>
                    {r.location ? <> · {r.location}</> : null}
                  </footer>
                </article>
              ))}
            </div>
          </div>

          {/* RIGHT: embedded map */}
          <div>
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute -inset-4 rounded-3xl bg-gradient-gold opacity-20 blur-2xl"
              />
              <div className="relative overflow-hidden rounded-3xl border border-border bg-card shadow-elegant">
                <div className="aspect-[4/3] w-full">
                  <iframe
                    title="Colorado Web Impressions on Google Maps"
                    src={EMBED_SRC}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="h-full w-full border-0"
                    allowFullScreen
                  />
                </div>
                <div className="border-t border-border bg-card p-5">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 flex-shrink-0 text-brand-gold-deep" aria-hidden="true" />
                    <div className="text-sm">
                      <div className="font-semibold text-foreground">
                        Serving all of Colorado from Colorado Springs
                      </div>
                      <div className="text-muted-foreground">
                        Front Range · Denver Metro · Mountain Towns
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <noscript>
              <p className="mt-3 text-center text-sm text-muted-foreground">
                <a href={DIRECTIONS_HREF} className="underline">
                  Open {HQ.name} in Google Maps
                </a>
              </p>
            </noscript>
          </div>
        </div>
      </div>
    </section>
  );
}
